import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "armiq_cookie_consent";  // values: "accepted" | "essential"

/**
 * GDPR / cookie consent banner — appears on every page until the user clicks
 * a choice. Choice is persisted in localStorage; the banner never re-prompts
 * unless the user clears storage. v1 ships without granular cookie categories
 * (we currently only set first-party session cookies + Stripe checkout cookies
 * during purchase). Adding a third-party analytics SDK later → re-prompt by
 * bumping the STORAGE_KEY suffix.
 */
export default function CookieConsent() {
  const [choice, setChoice] = useState(undefined);

  useEffect(() => {
    try { setChoice(localStorage.getItem(STORAGE_KEY) || null); }
    catch { setChoice(null); }
  }, []);

  const persist = (value) => {
    try { localStorage.setItem(STORAGE_KEY, value); } catch (err) { console.warn("[cookie-consent] storage write failed:", err); }
    setChoice(value);
  };

  if (choice === undefined || choice) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-50 rounded-2xl shadow-2xl ring-1 ring-white/10 p-5"
      style={{ backgroundColor: "var(--ink-2)" }}
      role="dialog"
      aria-live="polite"
      data-testid="cookie-consent"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(217,4,41,0.15)" }}>
          <Cookie className="h-4 w-4" style={{ color: "var(--brand-secondary)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-white text-base">Cookies &amp; privacy</div>
          <p className="text-xs text-white/60 mt-1 leading-relaxed">
            BioHacker Sports uses essential cookies to keep you logged in and to process payments securely.
            We don&apos;t use third-party advertising trackers. Read our{" "}
            <Link to="/privacy" className="underline hover:text-white">Privacy Policy</Link> for details.
          </p>
          <div className="mt-4 flex gap-2 flex-wrap">
            <Button size="sm" onClick={() => persist("accepted")} className="text-white font-bold red-glow rounded-full" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="cookie-accept">
              Accept all
            </Button>
            <Button size="sm" variant="outline" onClick={() => persist("essential")} className="border-white/15 text-white bg-white/5 hover:bg-white/10 rounded-full" data-testid="cookie-essential">
              Essential only
            </Button>
          </div>
        </div>
        <button
          onClick={() => persist("essential")}
          className="text-white/40 hover:text-white shrink-0"
          aria-label="Dismiss"
          data-testid="cookie-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
